/**
 * PassportLandingPage — Promoción pública del Pasaporte Viajero (/pasaporte)
 * Visible sin login: imagen promocional del pasaporte, beneficios y CTA de registro.
 * Si el usuario ya tiene sesión, redirige directamente a /passport.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Grid, Stack } from "@mui/material";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";

import { useFirebase } from "../context/ApiContext";
import { DashboardLayout } from "../components/Dashboard";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";
import SeoHead from "../components/Seo/SeoHead";
import { getSiteOrigin } from "../config/site";
import { breadcrumbSchema, organizationSchema, websiteSchema } from "../utils/seoSchema";

import "../components/Dashboard/Dashboard.css";

const ACCENT = "#8b6914";

const FEATURES = [
	{
		icon: <PublicOutlinedIcon sx={{ fontSize: 26 }} />,
		title: "Sellos por país",
		text: "Añade cada destino que visitas y acumula sellos como en un pasaporte real.",
	},
	{
		icon: <CollectionsBookmarkOutlinedIcon sx={{ fontSize: 26 }} />,
		title: "Portada personalizada",
		text: "Tu nombre en la cubierta, con diseño de piel y estampado dorado.",
	},
	{
		icon: <PhotoCameraOutlinedIcon sx={{ fontSize: 26 }} />,
		title: "Fotos y notas",
		text: "Sube fotos de cada viaje y guarda anotaciones en la página de cada país.",
	},
	{
		icon: <ShareOutlinedIcon sx={{ fontSize: 26 }} />,
		title: "Comparte tu pasaporte",
		text: "Genera un enlace público para que cualquiera vea tus viajes.",
	},
	{
		icon: <EmojiEventsOutlinedIcon sx={{ fontSize: 26 }} />,
		title: "Contador de sellos",
		text: "Lleva la cuenta de cuántos países has visitado.",
	},
	{
		icon: <EditNoteOutlinedIcon sx={{ fontSize: 26 }} />,
		title: "Edita cuando quieras",
		text: "Añade ciudades, cambia fechas o sube más fotos en cualquier momento.",
	},
];

const PassportLandingPage = () => {
	const navigate = useNavigate();
	const { user, handleLogout: contextLogout } = useFirebase();

	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("signup");

	if (user) {
		navigate("/passport", { replace: true });
		return null;
	}

	const formattedUser = null;

	const handleSectionChange = (sectionId) => {
		if (sectionId === "home" || sectionId === "recent") navigate("/");
		else if (sectionId === "explore") navigate("/explorar");
		else if (sectionId === "passport") navigate("/passport");
		else if (sectionId === "create") navigate("/crear-guia");
		else navigate("/");
	};

	const handleLogout = async () => {
		try {
			await contextLogout();
			navigate("/");
		} catch (e) {
			console.error(e);
		}
	};

	const openSignUp = () => {
		setAuthMode("signup");
		setShowLogin(true);
	};

	const openLogin = () => {
		setAuthMode("login");
		setShowLogin(true);
	};

	return (
		<>
			<SeoHead
				title="Pasaporte Viajero — tu diario de viajes digital"
				description="Colecciona sellos de cada país que visitas, sube fotos, comparte tu pasaporte y lleva la cuenta de tus aventuras. Gratis en Viajes Molones."
				path="/pasaporte"
				imagePath="/pasaporte-promo.png"
				jsonLd={{
					"@context": "https://schema.org",
					"@graph": [
						organizationSchema(getSiteOrigin()),
						websiteSchema(getSiteOrigin()),
						breadcrumbSchema(getSiteOrigin(), [
							{ name: "Inicio", path: "/" },
							{ name: "Pasaporte viajero", path: "/pasaporte" },
						]),
					],
				}}
			/>
			<DashboardLayout
				user={formattedUser}
				activeSection="passport"
				onSectionChange={handleSectionChange}
				onLogin={openLogin}
				onSignUp={openSignUp}
				onLogout={handleLogout}
				onCreateNew={() => navigate("/crear-guia")}
				onSearch={() => {}}
				prebuiltGuides={[]}
				myGuides={[]}
				imageMap={{}}
			>
				<Box sx={{ mt: { xs: 2, md: 0 }, maxWidth: 1040, mx: "auto" }}>
					{/* Hero */}
					<Box
						sx={{
							position: "relative",
							borderRadius: 4,
							overflow: "hidden",
							border: "1px solid #ececf4",
							backgroundColor: "#fff",
							boxShadow: "0 24px 60px -30px rgba(15, 23, 42, 0.22)",
							mb: { xs: 4, md: 6 },
						}}
					>
						<Box
							sx={{
								background:
									"linear-gradient(135deg, rgba(139,105,20,0.10) 0%, rgba(201,168,76,0.08) 48%, rgba(92,74,50,0.04) 100%)",
								px: { xs: 2.5, md: 5 },
								py: { xs: 3, md: 5 },
							}}
						>
							<Grid container spacing={3} alignItems="center">
								<Grid item xs={12} md={7}>
									<Typography
										variant="h4"
										component="h1"
										sx={{
											fontWeight: 800,
											letterSpacing: "-0.03em",
											color: "#0f172a",
											fontSize: {
												xs: "1.65rem",
												sm: "2rem",
												md: "2.35rem",
											},
											lineHeight: 1.15,
											mb: 2,
										}}
									>
										Tu Pasaporte Viajero: cada viaje deja huella
									</Typography>
									<Typography
										sx={{
											color: "#475569",
											fontSize: { xs: "1rem", md: "1.0625rem" },
											lineHeight: 1.65,
											mb: 3,
											maxWidth: 560,
										}}
									>
										Un pasaporte digital donde coleccionas sellos de cada país
										que visitas, guardas fotos y notas, y lo compartes con quien
										quieras. Todo gratis, desde tu cuenta de Viajes Molones.
									</Typography>

									<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
										<Button
											variant="contained"
											size="large"
											disableElevation
											onClick={openSignUp}
											sx={{
												border: "none !important",
												color: "#fff !important",
												background: `linear-gradient(135deg, ${ACCENT} 0%, #a07820 100%) !important`,
												fontWeight: 700,
												px: 4,
												py: 1.25,
												borderRadius: 3,
												textTransform: "none",
												fontSize: "1rem",
												boxShadow: `0 12px 28px -10px rgba(139,105,20,0.4) !important`,
												"&.MuiButton-containedPrimary": {
													background: `linear-gradient(135deg, ${ACCENT} 0%, #a07820 100%) !important`,
													color: "#fff !important",
												},
												"&:hover": {
													background: `linear-gradient(135deg, #7a5c12 0%, ${ACCENT} 100%) !important`,
													color: "#fff !important",
												},
												"&.MuiButton-containedPrimary:hover": {
													background: `linear-gradient(135deg, #7a5c12 0%, ${ACCENT} 100%) !important`,
													color: "#fff !important",
												},
											}}
										>
											Crear mi pasaporte gratis
										</Button>
										<Button
											variant="outlined"
											size="large"
											onClick={openLogin}
											sx={{
												borderColor: `${ACCENT} !important`,
												color: `${ACCENT} !important`,
												fontWeight: 600,
												px: 3,
												py: 1.25,
												borderRadius: 3,
												textTransform: "none",
												fontSize: "1rem",
												"&:hover": {
													borderColor: "#7a5c12 !important",
													backgroundColor: "rgba(139,105,20,0.06) !important",
													color: "#7a5c12 !important",
												},
											}}
										>
											Ya tengo cuenta
										</Button>
									</Stack>
								</Grid>

								{/* Imagen promocional (portada + página de sellos) */}
								<Grid item xs={12} md={5}>
									<Box
										sx={{
											mx: "auto",
											maxWidth: { xs: "100%", md: 440 },
											borderRadius: 3,
											overflow: "hidden",

											lineHeight: 0,
										}}
									>
										<Box
											component="img"
											src={`${process.env.PUBLIC_URL || ""}/pasaporte-promo.png`}
											alt="Pasaporte Viajes Molones con sellos de Japón, Corea del Sur, Maldivas y más"
											sx={{
												width: "100%",
												height: "auto",
												display: "block",
												verticalAlign: "middle",
											}}
										/>
									</Box>
								</Grid>
							</Grid>
						</Box>
					</Box>

					{/* Features */}
					<Typography
						variant="h5"
						sx={{
							fontWeight: 700,
							color: "#1a1a2e",
							mb: 1,
							fontSize: { xs: "1.25rem", md: "1.5rem" },
						}}
					>
						Qué incluye tu pasaporte
					</Typography>
					<Typography sx={{ color: "#64748b", mb: 3, maxWidth: 720 }}>
						Todo lo que necesitas para registrar tus viajes en un solo lugar.
					</Typography>

					<Grid container spacing={2.5} sx={{ mb: 5 }}>
						{FEATURES.map((item) => (
							<Grid item xs={12} sm={6} key={item.title}>
								<Box
									sx={{
										height: "100%",
										p: 2.5,
										borderRadius: 3,
										border: "1px solid rgba(0,0,0,0.06)",
										backgroundColor: "#fff",
										transition: "box-shadow .2s ease, transform .2s ease",
										"&:hover": {
											boxShadow: "0 12px 32px -18px rgba(15, 23, 42, 0.25)",
											transform: { sm: "translateY(-2px)" },
										},
									}}
								>
									<Box
										sx={{
											width: 48,
											height: 48,
											borderRadius: "14px",
											background:
												"linear-gradient(135deg, rgba(139,105,20,0.15) 0%, rgba(201,168,76,0.1) 100%)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: ACCENT,
											mb: 1.5,
										}}
									>
										{item.icon}
									</Box>
									<Typography
										sx={{
											fontWeight: 700,
											color: "#0f172a",
											mb: 0.75,
										}}
									>
										{item.title}
									</Typography>
									<Typography
										sx={{
											color: "#64748b",
											fontSize: "0.9375rem",
											lineHeight: 1.55,
										}}
									>
										{item.text}
									</Typography>
								</Box>
							</Grid>
						))}
					</Grid>

					{/* CTA final */}
					<Box
						sx={{
							borderRadius: 4,
							p: { xs: 3, md: 4 },
							background: `linear-gradient(135deg, ${ACCENT} 0%, #7a5c12 55%, #5c4a32 100%)`,
							color: "#fff",
							textAlign: "center",
							boxShadow: "0 24px 50px -28px rgba(139,105,20,0.55)",
							mb: 6,
						}}
					>
						<Typography
							variant="h5"
							sx={{
								fontWeight: 800,
								mb: 1.5,
								fontSize: { xs: "1.25rem", md: "1.5rem" },
							}}
						>
							Empieza a coleccionar sellos
						</Typography>
						<Typography
							sx={{
								opacity: 0.9,
								mb: 3,
								maxWidth: 520,
								mx: "auto",
								lineHeight: 1.6,
							}}
						>
							Crea tu cuenta gratuita, abre tu pasaporte y añade el primer
							destino. En dos minutos lo tienes listo.
						</Typography>
						<Button
							variant="contained"
							size="large"
							disableElevation
							onClick={openSignUp}
							sx={{
								fontWeight: 700,
								px: 4,
								py: 1.25,
								borderRadius: 3,
								textTransform: "none",
								fontSize: "1rem",
								border: "none !important",
								boxShadow: "0 8px 20px -8px rgba(0,0,0,0.28) !important",
								/* index.css fuerza .MuiButton-containedPrimary a azul con !important */
								"&.MuiButton-containedPrimary": {
									backgroundColor: "#fff !important",
									color: `${ACCENT} !important`,
								},
								"&:hover": {
									backgroundColor: "#f5f5f0 !important",
									color: `${ACCENT} !important`,
									boxShadow: "0 10px 24px -8px rgba(0,0,0,0.35) !important",
								},
								"&.MuiButton-containedPrimary:hover": {
									backgroundColor: "#f5f5f0 !important",
									color: `${ACCENT} !important`,
								},
								"&:focus-visible": {
									backgroundColor: "#fff !important",
									color: `${ACCENT} !important`,
									outline: `2px solid rgba(255,255,255,0.85)`,
									outlineOffset: 2,
								},
							}}
						>
							Crear mi pasaporte gratis
						</Button>
					</Box>
				</Box>
			</DashboardLayout>

			{/* Modal Auth */}
			<BasicModal
				width={500}
				showModal={showLogin}
				handleClose={() => setShowLogin(false)}
			>
				<Auth setShowLogin={setShowLogin} initialMode={authMode} />
			</BasicModal>
		</>
	);
};

export default PassportLandingPage;
